import { useState, useEffect, useRef } from 'react';
import { Send, Shield, Search, CheckCircle2, Phone, MessageCircle, AlertTriangle, UserPlus, Paperclip, X, File, Mic, Users } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import api from '@/services/api';
import { toast } from 'sonner';

interface SignalMessage {
    id: string;
    senderNumber: string;
    receiverNumber: string;
    groupId?: string;
    textContent: string;
    isIncoming: boolean;
    createdAt: string;
    attachmentId?: string;
    attachmentType?: string;
    attachmentName?: string;
}

interface SignalContact {
    id: string;
    name: string;
    number: string;
}

interface SignalGroup {
    id: string;
    groupId: string;
    name: string;
    members: string;
}

export default function SignalChatPage() {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<SignalMessage[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [targetNumber, setTargetNumber] = useState('+569');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [attachedFile, setAttachedFile] = useState<globalThis.File | null>(null);

    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<number | null>(null);

    const [groups, setGroups] = useState<SignalGroup[]>([]);
    const [contacts, setContacts] = useState<SignalContact[]>([]);

    const [isCreatingGroup, setIsCreatingGroup] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupMembers, setNewGroupMembers] = useState<string[]>([]);

    const [isAddingMembers, setIsAddingMembers] = useState(false);
    const [membersToAdd, setMembersToAdd] = useState<string[]>([]);

    const [isViewingMembers, setIsViewingMembers] = useState(false);
    const [viewingGroupId, setViewingGroupId] = useState<string | null>(null);
    const [removingMember, setRemovingMember] = useState<string | null>(null);

    const [isCreatingContact, setIsCreatingContact] = useState(false);
    const [newContactName, setNewContactName] = useState('');

    const activeGroupId = groups.find(g => g.groupId === targetNumber)?.groupId;

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        // Load initial history
        fetchHistory();

        // Connect to SSE stream
        const url = import.meta.env.VITE_API_URL || '';
        const eventSource = new EventSource(`${url}/api/v1/signal/stream`, {
            withCredentials: true
        });

        eventSource.addEventListener('message', (event) => {
            try {
                const newMsg = JSON.parse(event.data);
                setMessages(prev => {
                    if (prev.find(m => m.id === newMsg.id)) return prev;
                    return [...prev, newMsg];
                });
                setTimeout(scrollToBottom, 50);
            } catch (error) {
                console.error('Error parsing SSE message', error);
            }
        });

        return () => eventSource.close();
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const fetchHistory = async () => {
        try {
            const res = await api.get('/api/v1/signal/history');
            setMessages(res.data);
            const groupsRes = await api.get('/api/v1/signal/groups');
            setGroups(groupsRes.data);
            const contactsRes = await api.get('/api/v1/signal/contacts');
            setContacts(contactsRes.data);
        } catch (error) {
            console.error('Failed to load history', error);
            toast.error(t('signal.toast.loadError'));
        }
    };

    const handleCreateGroup = async () => {
        if (!newGroupName.trim() || newGroupMembers.length === 0) {
            toast.error(t('signal.toast.groupNameRequired'));
            return;
        }
        setLoading(true);
        try {
            await api.post('/api/v1/signal/groups', {
                name: newGroupName,
                members: newGroupMembers
            });
            toast.success(t('signal.toast.groupCreated'));
            setIsCreatingGroup(false);
            setNewGroupName('');
            setNewGroupMembers([]);
            fetchHistory(); // Refresh groups
        } catch (error: any) {
            console.error(error);
            const backendMsg = error?.response?.data?.message || '';
            if (backendMsg.includes('not registered') || backendMsg.includes('Error proveedor')) {
                toast.error(t('signal.toast.signalNotInstalledGroup'), { duration: 6000 });
            } else {
                toast.error(t('signal.toast.groupCreateError', { detail: backendMsg || t('signal.unknownError') }));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAddMembersToGroup = async () => {
        if (!activeGroupId || membersToAdd.length === 0) {
            toast.error(t('signal.toast.selectContactToAdd'));
            return;
        }
        setLoading(true);
        try {
            await api.put(`/api/v1/signal/groups/${activeGroupId}/members`, {
                members: membersToAdd
            });
            toast.success(t('signal.toast.membersAdded'));
            setIsAddingMembers(false);
            setMembersToAdd([]);
            fetchHistory();
        } catch (error: any) {
            console.error(error);
            const backendMsg = error?.response?.data?.message || '';
            if (backendMsg.includes('not registered') || backendMsg.includes('Error proveedor')) {
                toast.error(t('signal.toast.signalNotInstalled'), { duration: 6000 });
            } else {
                toast.error(t('signal.toast.addMembersError', { detail: backendMsg || t('signal.unknownError') }));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMember = async (groupId: string, memberNumber: string) => {
        if (!groupId || !memberNumber) {
            console.error('handleRemoveMember: groupId o memberNumber inválido', { groupId, memberNumber });
            return;
        }
        setRemovingMember(memberNumber);
        try {
            await api.delete(`/api/v1/signal/groups/${groupId}/members`, {
                data: { members: [memberNumber] }
            });
            toast.success(t('signal.toast.memberRemoved'));
            await fetchHistory();
        } catch (error: any) {
            console.error('Error al eliminar miembro:', error);
            toast.error(t('signal.toast.removeMemberError', { detail: error?.response?.data?.message || error?.message || t('signal.unknownError') }));
        } finally {
            setRemovingMember(null);
        }
    };

    const handleCreateContact = async () => {
        if (!newContactName.trim() || targetNumber.length < 11) {
            toast.error(t('signal.toast.contactNameOrNumberInvalid'));
            return;
        }
        setLoading(true);
        try {
            await api.post('/api/v1/signal/contacts', {
                name: newContactName,
                number: targetNumber
            });
            toast.success(t('signal.toast.contactRegistered'));
            setIsCreatingContact(false);
            setNewContactName('');
            fetchHistory(); // Refresh contacts
        } catch (error) {
            console.error(error);
            toast.error(t('signal.toast.contactRegisterError'));
        } finally {
            setLoading(false);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() && !attachedFile) {
            toast.error(t('signal.toast.messageOrFileRequired'));
            return;
        }

        if (!activeGroupId && targetNumber.length < 11) {
            toast.error(t('signal.toast.recipientRequired'));
            return;
        }

        setLoading(true);
        try {
            const payload: any = { message: newMessage || '' };
            if (activeGroupId) {
                payload.groupId = activeGroupId;
            } else {
                payload.recipients = [targetNumber];
            }

            const sendPayload = async (finalPayload: any) => {
                await api.post('/api/v1/signal/send', finalPayload);
                setNewMessage('');
                setAttachedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                toast.success(t('signal.toast.sendSuccess'));
            };

            if (attachedFile) {
                const reader = new FileReader();
                reader.readAsDataURL(attachedFile);
                reader.onload = async () => {
                    const base64full = reader.result as string;
                    const base64data = base64full.split(',')[1];
                    payload.base64Attachment = base64data;
                    payload.attachmentName = attachedFile.name;
                    payload.attachmentType = attachedFile.type;
                    await sendPayload(payload);
                };
                reader.onerror = () => {
                    toast.error(t('signal.toast.fileProcessError'));
                    setLoading(false);
                };
            } else {
                await sendPayload(payload);
            }
        } catch (error) {
            console.error(error);
            toast.error(t('signal.toast.sendError'));
        } finally {
            if (!attachedFile) {
                setLoading(false);
            } else {
                // If attachedFile, the onload handles sending, but state unmount inside onload might cause issues. We'll set timeout fallback or the finally block executes immediately before onload.. 
                // We'll set loading false when the api call resolves up in the sendPayload via a wrapper. But a simpler way is just to unlock it after resolving.
                setTimeout(() => setLoading(false), 2000);
            }
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    audioChunksRef.current.push(e.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const file = new globalThis.File([audioBlob], `Audio_Tactico_${new Date().getTime()}.webm`, { type: 'audio/webm' });
                setAttachedFile(file);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);
            timerRef.current = window.setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } catch (error) {
            console.error('Error accessing mic:', error);
            toast.error(t('signal.toast.micPermissionDenied'));
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (timerRef.current) clearInterval(timerRef.current);
        }
    };

    return (
        <>
            {isCreatingContact && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-indigo-500/30 w-full max-w-md rounded-2xl p-6 shadow-2xl">
                        <h2 className="text-white text-lg font-black uppercase mb-4">{t('signal.registerContact')}</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">{t('signal.contactName')}</label>
                                <input
                                    value={newContactName} onChange={(e) => setNewContactName(e.target.value)}
                                    className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-white outline-none focus:border-indigo-500"
                                    placeholder={t('signal.contactNamePlaceholder')}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">{t('signal.number')}</label>
                                <input
                                    value={targetNumber} readOnly
                                    className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 font-mono text-gray-400 outline-none cursor-not-allowed"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-8">
                            <button onClick={() => setIsCreatingContact(false)} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white uppercase font-bold tracking-wider">{t('signal.cancel')}</button>
                            <button onClick={handleCreateContact} disabled={loading} className="px-5 py-2 rounded-lg text-sm bg-indigo-600 hover:bg-indigo-500 text-white uppercase font-black tracking-wider shadow-[0_0_15px_rgba(99,102,241,0.4)] disabled:opacity-50">
                                {loading ? t('signal.saving') : t('signal.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isCreatingGroup && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-indigo-500/30 w-full max-w-md rounded-2xl p-6 shadow-2xl">
                        <h2 className="text-white text-lg font-black uppercase mb-4">{t('signal.createTacticalGroup')}</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">{t('signal.tacticalName')}</label>
                                <input
                                    value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
                                    className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-white outline-none focus:border-indigo-500"
                                    placeholder={t('signal.tacticalNamePlaceholder')}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">{t('signal.selectMembers', { count: newGroupMembers.length })}</label>
                                <div className="bg-black/30 border border-white/10 rounded-lg p-2 max-h-48 overflow-y-auto custom-scrollbar space-y-1">
                                    {contacts.length === 0 ? (
                                        <p className="text-xs text-gray-500 italic p-2">{t('signal.noContactsSaved')}</p>
                                    ) : (
                                        contacts.map(contact => {
                                            const isSelected = newGroupMembers.includes(contact.number);
                                            return (
                                                <div
                                                    key={contact.id}
                                                    onClick={() => {
                                                        if (isSelected) {
                                                            setNewGroupMembers(prev => prev.filter(n => n !== contact.number));
                                                        } else {
                                                            setNewGroupMembers(prev => [...prev, contact.number]);
                                                        }
                                                    }}
                                                    className={`cursor-pointer px-3 py-2 rounded-md flex justify-between items-center transition-all ${isSelected ? 'bg-indigo-500/20 text-indigo-300' : 'hover:bg-white/5 text-gray-400'}`}
                                                >
                                                    <div>
                                                        <div className={`text-xs font-bold ${isSelected ? 'text-white' : ''} uppercase`}>{contact.name}</div>
                                                        <div className="text-[10px] font-mono">{contact.number}</div>
                                                    </div>
                                                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${isSelected ? 'bg-indigo-500 border-indigo-500' : 'border-white/20'}`}>
                                                        {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-8">
                            <button onClick={() => setIsCreatingGroup(false)} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white uppercase font-bold tracking-wider">{t('signal.cancel')}</button>
                            <button onClick={handleCreateGroup} disabled={loading} className="px-5 py-2 rounded-lg text-sm bg-indigo-600 hover:bg-indigo-500 text-white uppercase font-black tracking-wider shadow-[0_0_15px_rgba(99,102,241,0.4)] disabled:opacity-50">
                                {loading ? t('signal.creating') : t('signal.establishGroup')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isViewingMembers && viewingGroupId && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-indigo-500/30 w-full max-w-md rounded-2xl p-6 shadow-2xl">
                        <div className="flex items-center justify-between mb-1">
                            <h2 className="text-white text-lg font-black uppercase">{t('signal.groupMembers')}</h2>
                            <button onClick={() => { setIsViewingMembers(false); setViewingGroupId(null); }} className="p-1 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <p className="text-xs text-gray-400 mb-4 truncate italic">{groups.find(g => g.groupId === viewingGroupId)?.name}</p>
                        <div className="bg-black/30 border border-white/10 rounded-lg p-2 max-h-72 overflow-y-auto custom-scrollbar space-y-1">
                            {(() => {
                                const currentGroup = groups.find(g => g.groupId === viewingGroupId);
                                const memberNumbers = currentGroup?.members.split(',').map(m => m.trim()).filter(Boolean) || [];
                                if (memberNumbers.length === 0) {
                                    return <p className="text-xs text-gray-500 italic p-2 text-center py-4">{t('signal.noMembersRegistered')}</p>;
                                }
                                return memberNumbers.map((num, idx) => {
                                    const contact = contacts.find(c => c.number === num);
                                    const isRemoving = removingMember === num;
                                    return (
                                        <div key={idx} className="flex items-center justify-between px-3 py-2.5 rounded-md hover:bg-white/5 transition-all">
                                            <div>
                                                <div className="text-xs font-bold text-white uppercase">{contact?.name || num}</div>
                                                <div className="text-[10px] font-mono text-gray-400">{num}</div>
                                            </div>
                                            <button
                                                onClick={() => handleRemoveMember(viewingGroupId, num)}
                                                disabled={removingMember !== null}
                                                title={t('signal.removeFromGroup')}
                                                className="p-1.5 hover:bg-red-500/20 rounded-lg text-gray-500 hover:text-red-400 transition-colors disabled:opacity-40"
                                            >
                                                {isRemoving
                                                    ? <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                                                    : <X className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                        <div className="flex justify-end mt-6">
                            <button onClick={() => { setIsViewingMembers(false); setViewingGroupId(null); }} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white uppercase font-bold tracking-wider">{t('signal.close')}</button>
                        </div>
                    </div>
                </div>
            )}

            {isAddingMembers && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-indigo-500/30 w-full max-w-md rounded-2xl p-6 shadow-2xl">
                        <h2 className="text-white text-lg font-black uppercase mb-1">{t('signal.addTacticals')}</h2>
                        <p className="text-xs text-gray-400 mb-4 truncate italic">{t('signal.currentGroup', { name: groups.find(g => g.groupId === activeGroupId)?.name })}</p>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">{t('signal.selectedPersonnel', { count: membersToAdd.length })}</label>
                                <div className="bg-black/30 border border-white/10 rounded-lg p-2 max-h-56 overflow-y-auto custom-scrollbar space-y-1">
                                    {contacts.length === 0 ? (
                                        <p className="text-xs text-gray-500 italic p-2">{t('signal.noContactsToAdd')}</p>
                                    ) : (
                                        (() => {
                                            const currentGroup = groups.find(g => g.groupId === activeGroupId);
                                            const currentMemberNumbers = currentGroup?.members.split(',').map(m => m.trim()) || [];
                                            const availableContacts = contacts.filter(c => !currentMemberNumbers.includes(c.number));

                                            if (availableContacts.length === 0) {
                                                return <p className="text-xs text-gray-500 italic p-2 text-center py-4">{t('signal.allContactsInGroup')}</p>;
                                            }

                                            return availableContacts.map(contact => {
                                                const isSelected = membersToAdd.includes(contact.number);
                                                return (
                                                    <div
                                                        key={contact.id}
                                                        onClick={() => {
                                                            if (isSelected) {
                                                                setMembersToAdd(prev => prev.filter(n => n !== contact.number));
                                                            } else {
                                                                setMembersToAdd(prev => [...prev, contact.number]);
                                                            }
                                                        }}
                                                        className={`cursor-pointer px-3 py-2 rounded-md flex justify-between items-center transition-all ${isSelected ? 'bg-indigo-500/20 text-indigo-300' : 'hover:bg-white/5 text-gray-400'}`}
                                                    >
                                                        <div>
                                                            <div className={`text-xs font-bold ${isSelected ? 'text-white' : ''} uppercase`}>{contact.name}</div>
                                                            <div className="text-[10px] font-mono">{contact.number}</div>
                                                        </div>
                                                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${isSelected ? 'bg-indigo-500 border-indigo-500' : 'border-white/20'}`}>
                                                            {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })()
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-8">
                            <button onClick={() => { setIsAddingMembers(false); setMembersToAdd([]); }} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white uppercase font-bold tracking-wider">{t('signal.cancel')}</button>
                            <button onClick={handleAddMembersToGroup} disabled={loading} className="px-5 py-2 rounded-lg text-sm bg-indigo-600 hover:bg-indigo-500 text-white uppercase font-black tracking-wider shadow-[0_0_15px_rgba(99,102,241,0.4)] disabled:opacity-50">
                                {loading ? t('signal.adding') : t('signal.addToGroup')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="h-[calc(100vh-8rem)] bg-gray-900 border border-white/5 rounded-3xl overflow-hidden shadow-2xl flex relative">

                {/* Left Sidebar - Contacts/Groups */}
                <div className="w-80 border-r border-white/5 bg-gray-900/50 flex flex-col hidden md:flex">
                    <div className="p-6 border-b border-white/5 bg-white/[0.02]">
                        <h2 className="text-sm font-black text-white uppercase tracking-[0.2em] flex items-center gap-3">
                            <MessageCircle className="w-5 h-5 text-indigo-400" />
                            {t('signal.title')}
                        </h2>
                        <p className="text-[10px] text-gray-500 mt-2 uppercase tracking-widest font-bold">{t('signal.subtitle')}</p>
                    </div>
                    <div className="p-4 border-b border-white/5 flex gap-2">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                placeholder={t('signal.searchPlaceholder')}
                                className="w-full bg-black/20 border border-white/10 rounded-xl px-10 py-3 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-indigo-500/50"
                            />
                            <Search className="w-4 h-4 text-gray-500 absolute left-4 top-3.5" />
                        </div>
                        <button onClick={() => setIsCreatingGroup(true)} className="w-10 h-10 bg-indigo-500/20 border border-indigo-500/40 rounded-xl flex items-center justify-center hover:bg-indigo-500 text-indigo-400 hover:text-white transition-all shadow-[0_0_15px_rgba(99,102,241,0.2)]">
                            +
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-1">
                        <div className="px-3 pb-2 pt-1 text-[10px] uppercase font-bold text-gray-500 tracking-wider">{t('signal.directContacts')}</div>
                        {contacts.map((contact) => (
                            <div
                                key={contact.id}
                                onClick={() => setTargetNumber(contact.number)}
                                className={`p-3 border rounded-xl cursor-pointer transition-all flex items-center gap-4 ${targetNumber === contact.number
                                    ? 'bg-indigo-500/20 border-indigo-500/40 shadow-[0_0_15px_rgba(99,102,241,0.1)]'
                                    : 'bg-transparent border-transparent hover:bg-white/5'
                                    }`}
                            >
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-indigo-500/10 border border-indigo-500/20`}>
                                    <Shield className="w-5 h-5 text-indigo-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-black text-white uppercase truncate">{contact.name}</div>
                                    <div className={`text-[10px] font-mono mt-0.5 truncate ${targetNumber === contact.number ? 'text-indigo-300' : 'text-gray-500'}`}>
                                        {contact.number}
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="px-3 pb-2 pt-5 text-[10px] uppercase font-bold text-gray-500 tracking-wider">{t('signal.tacticalGroups')}</div>
                        {groups.map((group) => (
                            <div
                                key={group.id}
                                onClick={() => setTargetNumber(group.groupId)}
                                className={`p-3 border rounded-xl cursor-pointer transition-all flex items-center gap-4 ${targetNumber === group.groupId
                                    ? 'bg-indigo-500/20 border-indigo-500/40 shadow-[0_0_15px_rgba(99,102,241,0.1)]'
                                    : 'bg-transparent border-transparent hover:bg-white/5'
                                    }`}
                            >
                                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-800">
                                    <AlertTriangle className="w-5 h-5 text-gray-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-black text-white uppercase truncate">{group.name}</div>
                                    <div className={`text-[10px] font-mono mt-0.5 truncate ${targetNumber === group.groupId ? 'text-indigo-300' : 'text-gray-500'}`}>
                                        {group.members.substring(0, 15)}...
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Main Chat Area */}
                <div className="flex-1 flex flex-col bg-[#050608] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/10 via-gray-900 to-black relative">

                    {/* Chat Header */}
                    <div className="h-20 border-b border-white/5 px-6 flex flex-col lg:flex-row lg:items-center justify-between bg-black/20 backdrop-blur-md z-10 shrink-0">
                        <div className="flex items-center gap-4 mb-3 lg:mb-0">
                            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20 shadow-[0_0_15px_rgba(99,102,241,0.2)]">
                                <Shield className="w-5 h-5 text-indigo-400" />
                            </div>
                            <div>
                                <h3 className="text-sm font-black text-white uppercase tracking-wider">
                                    {activeGroupId ? groups.find(g => g.groupId === activeGroupId)?.name : t('signal.commandCenterSignal')}
                                </h3>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full shadow-[0_0_8px_#22c55e] animate-pulse" />
                                    <span className="text-[10px] text-green-400 font-mono font-bold">
                                        {activeGroupId
                                            ? t('signal.membersInNetwork', { count: groups.find(g => g.groupId === activeGroupId)?.members.split(',').length || 0 })
                                            : t('signal.encryptedConnection')}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 w-full lg:w-auto">
                            {activeGroupId && (
                                <>
                                    <button onClick={() => { setViewingGroupId(activeGroupId); setIsViewingMembers(true); }} title={t('signal.viewRemoveMembers')} className="w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl flex items-center justify-center text-gray-400 hover:text-white transition-all shrink-0">
                                        <Users className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => setIsAddingMembers(true)} title={t('signal.addMembersToGroup')} className="w-10 h-10 bg-indigo-500/20 hover:bg-indigo-500/40 border border-indigo-500/40 rounded-xl flex items-center justify-center text-indigo-400 hover:text-white transition-all shrink-0">
                                        <UserPlus className="w-4 h-4" />
                                    </button>
                                </>
                            )}
                            <div className="bg-black/40 border border-white/5 rounded-xl flex items-center px-4 py-2 w-full lg:w-64">
                                <Phone className="w-3.5 h-3.5 text-gray-400 mr-3 shrink-0" />
                                <input
                                    type="text"
                                    value={targetNumber}
                                    onChange={(e) => setTargetNumber(e.target.value)}
                                    className="bg-transparent text-xs text-white focus:outline-none w-full font-mono"
                                    placeholder="+56900000000"
                                    disabled={!!activeGroupId}
                                />
                            </div>
                            {!activeGroupId && (
                                <button onClick={() => setIsCreatingContact(true)} title={t('signal.registerDestinationContact')} className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-xl flex items-center justify-center text-gray-400 hover:text-emerald-400 transition-colors shrink-0">
                                    <UserPlus className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Messages Timeline */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                        {messages.filter(m => m.groupId === targetNumber || m.senderNumber === targetNumber || m.receiverNumber === targetNumber).length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-gray-500 opacity-50 space-y-4">
                                <Shield className="w-16 h-16 text-indigo-900" />
                                <div className="text-center">
                                    <p className="text-sm font-black uppercase tracking-widest text-indigo-400">{t('signal.emptyChannel')}</p>
                                    <p className="text-[10px] uppercase font-mono mt-2"><Trans i18nKey="signal.emptyChannelHint">Los mensajes enviados por este medio<br />no son visibles en servidores intermedios.</Trans></p>
                                </div>
                            </div>
                        ) : (
                            messages
                                .filter(m => m.groupId === targetNumber || m.senderNumber === targetNumber || m.receiverNumber === targetNumber)
                                .map((msg) => (
                                    <div key={msg.id} className={`flex flex-col ${msg.isIncoming ? 'items-start' : 'items-end'}`}>
                                        <div className="text-[9px] text-gray-500 font-mono mb-1.5 px-2">
                                            {msg.isIncoming ? msg.senderNumber : (activeGroupId ? t('signal.youToGroup') : t('signal.youTo', { number: msg.receiverNumber }))}
                                        </div>
                                        <div className={`relative max-w-[85%] md:max-w-xl px-5 py-3.5 rounded-2xl text-sm ${msg.isIncoming
                                            ? 'bg-gray-800 border border-white/10 text-white rounded-tl-sm'
                                            : 'bg-indigo-600 text-white rounded-tr-sm shadow-[0_5px_20px_rgba(79,70,229,0.3)]'
                                            }`}>
                                            <p className="whitespace-pre-wrap leading-relaxed">{msg.textContent}</p>
                                            {msg.attachmentId && (
                                                <div className="mt-2 text-xs border border-white/10 rounded-xl overflow-hidden bg-black/30 max-w-[250px]">
                                                    {msg.attachmentId.startsWith('LOCAL_') ? (
                                                        <div className="p-3 text-center text-gray-400 italic font-mono">{t('signal.processingAttachment')}</div>
                                                    ) : msg.attachmentType?.startsWith('image/') ? (
                                                        <img src={`${import.meta.env.VITE_API_URL || ''}/api/v1/signal/attachments/${msg.attachmentId}`} className="w-full h-auto object-cover" />
                                                    ) : msg.attachmentType?.startsWith('video/') ? (
                                                        <video src={`${import.meta.env.VITE_API_URL || ''}/api/v1/signal/attachments/${msg.attachmentId}`} controls className="w-full h-auto" />
                                                    ) : msg.attachmentType?.startsWith('audio/') ? (
                                                        <audio src={`${import.meta.env.VITE_API_URL || ''}/api/v1/signal/attachments/${msg.attachmentId}`} controls className="w-full" />
                                                    ) : (
                                                        <a href={`${import.meta.env.VITE_API_URL || ''}/api/v1/signal/attachments/${msg.attachmentId}?download=true&filename=${encodeURIComponent(msg.attachmentName || 'archivo')}`} target="_blank" rel="noreferrer" className="p-3 flex items-center gap-2 text-indigo-300 hover:text-indigo-200">
                                                            <Paperclip className="w-4 h-4 shrink-0" />
                                                            <span className="truncate">{msg.attachmentName || t('signal.attachedFile')}</span>
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                            <div className={`mt-2 text-[9px] font-bold tracking-wider flex items-center gap-1.5 justify-end ${msg.isIncoming ? 'text-gray-400' : 'text-indigo-200'}`}>
                                                {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                {!msg.isIncoming && <CheckCircle2 className="w-3 h-3 text-indigo-300" />}
                                            </div>
                                        </div>
                                    </div>
                                ))
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-4 bg-gray-900 border-t border-white/5 space-y-3">
                        {attachedFile && (
                            <div className="flex items-center gap-3 bg-indigo-900/30 border border-indigo-500/30 rounded-xl p-3 max-w-sm">
                                <File className="w-5 h-5 text-indigo-400 shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <p className="text-white text-xs truncate font-medium">{attachedFile.name}</p>
                                    <p className="text-gray-400 text-[10px] mt-0.5">{(attachedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                </div>
                                <button onClick={() => { setAttachedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="p-1 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                        <div className="bg-black/20 border border-white/10 rounded-2xl flex items-end p-2 focus-within:border-indigo-500/50 transition-colors shadow-inner">
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                        setAttachedFile(e.target.files[0]);
                                    }
                                }}
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="p-3 text-gray-400 hover:text-indigo-400 hover:bg-white/5 rounded-xl transition-all mb-1 shrink-0"
                                title={t('signal.attachFile')}
                            >
                                <Paperclip className="w-5 h-5" />
                            </button>
                            <button
                                onClick={isRecording ? stopRecording : startRecording}
                                className={`p-3 rounded-xl transition-all mb-1 shrink-0 ${isRecording ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30' : 'text-gray-400 hover:text-indigo-400 hover:bg-white/5'}`}
                                title={isRecording ? t('signal.stopRecording') : t('signal.recordVoiceNote')}
                            >
                                {isRecording ? <div className="w-5 h-5 flex items-center justify-center font-mono text-[10px] animate-pulse">{recordingTime}s</div> : <Mic className="w-5 h-5" />}
                            </button>
                            {isRecording ? (
                                <div className="flex-1 bg-transparent text-sm text-red-400 px-3 py-3 font-mono animate-pulse flex items-center h-[48px]">
                                    {t('signal.recordingAudio')}
                                </div>
                            ) : (
                                <textarea
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    placeholder={t('signal.messagePlaceholder')}
                                    className="flex-1 bg-transparent text-sm text-white px-3 py-3 max-h-32 focus:outline-none resize-none custom-scrollbar"
                                    rows={1}
                                    style={{ minHeight: '48px' }}
                                />
                            )}
                            <button
                                onClick={handleSendMessage}
                                disabled={(!newMessage.trim() && !attachedFile) || loading}
                                className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white p-3.5 rounded-xl transition-all hover:scale-105 active:scale-95 shadow-[0_0_15px_rgba(79,70,229,0.4)] mr-1 mb-1"
                            >
                                {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
